import { action, makeObservable, observable, runInAction, computed } from 'mobx';
import dayjs, { Dayjs } from 'dayjs';
import { DateTime } from 'luxon';
import { Error, pageError } from '../../core/error';
import { Maybe } from '../../core';
import { Store } from '../../store/store';
import { StoreNode } from '../../store';
import { Team } from '../../entities/team';

import { DateInputState, dateInput } from '../../components/input/dateInput/dateInputState';
import { RangeOptions } from '../../components/input/dateInput/dateInput';
import { 
  ReactionsChartState,
  TopicViewsChartState,
  BookmarksChartState,
  HoursCliprdChartState,
  AudienceFocusChartState,
  TeamPerformanceChartState,
  PerformanceChartState
} from '../../components/chart';
import { ChartPageParams } from '../../components/chart/chartSchema';
import { MetricsWrapperState } from '../../components/chart/metricsWrapper/metricsWrapperState';

export class AnalyticsPageState
  extends StoreNode {

  readonly reactionsChart = new ReactionsChartState(this.store, {
    params: () => (this.chartParams)
  });

  readonly bookmarksChart = new BookmarksChartState(this.store, {
    params: () => (this.chartParams)
  });

  readonly hoursCliprdChart = new HoursCliprdChartState(this.store, {
    params: () => (this.chartParams)
  });

  readonly audienceFocusChart = new AudienceFocusChartState(this.store, {
    params: () => (this.chartParams)
  });

  readonly topicViewsChart = new TopicViewsChartState(this.store, {
    params: () => (this.chartParams)
  });

  readonly teamPerformanceChart = new TeamPerformanceChartState(this.store, {
    params: () => (this.chartParams)
  });

  readonly performanceChart = new PerformanceChartState(this.store, {
    params: () => (this.chartParams)
  });

  readonly metricsWrapper = new MetricsWrapperState(this.store, {
    params: () => (this.chartParams)
  });

  readonly dateInput: DateInputState;
  readonly dateRangeOptions: RangeOptions[] = [{
    name: 'Today',
    getRange: () => {
      return [dayjs(), dayjs()];
    }
  },
  {
    name: 'Yesterday',
    getRange: () => {
      return [dayjs().subtract(1, 'day'), dayjs().subtract(1, 'day')];
    }
  },
  {
    name: 'Last Week',
    getRange: () => {
      return [dayjs().subtract(7, 'day'), dayjs()];
    }
  },
  {
    name: 'Last 30 Days',
    getRange: () => {
      return [dayjs().subtract(31, 'day'), dayjs()];
    }
  },
  {
    name: 'This Month',
    getRange: () => {
      return [dayjs().startOf('month'), dayjs().endOf('month')]
    }
  }];

  constructor(store: Store) {
    super(store);
    makeObservable<this, 'setError' | 'setLoaded'>(this, {
      endDateFilter: observable,
      startDateFilter: observable,
      timezone: observable,
      teamId: observable,
      team: observable,
      isLoading: observable,
      error: observable,
      pageSize: observable,
      chartParams: computed,
      mounted: action,
      unmounted: action,
      setPageSize: action,
      handleDateFilterChange: action,
      fetchData: action,
      reset: action,
      setError: action,
      setLoaded: action,
    });

    this.dateInput = dateInput(this, {
      placeholder: ['mm/dd/yyyy', 'mm/dd/yyyy'],
      format: ['MM/DD/YYYY', 'MMDDYYYY'],
      allowEmpty: [false, false],
      rangeOptions: this.dateRangeOptions,
      activeRangeOption: this.dateRangeOptions[2]
    });
  }

  endDateFilter: DateTime | null = null;
  startDateFilter: DateTime | null = null;
  timezone: string | null = null;
  teamId: string | null = null;
  team: Maybe<Team>;

  isLoading = false;
  error: Error | null = null;

  pageSize: number = 20;


  get chartParams(): ChartPageParams {
    return {
      startDate: this.startDateFilter,
      endDate: this.endDateFilter,
      timezone: this.timezone,
      teamId: this.teamId
    };
  }

  async mounted(teamId?: string) {
    this.reset();

    this.teamId = teamId ?? null;
    this.team = this.store.teamManager.getTeam(teamId);
    if (!this.team && this.teamId) {
      this.isLoading = true;
      const [team] = await this.store.teamManager.apiFetchTeam({ id: this.teamId });
      runInAction(() => {
        this.team = team;
      });
    }

    if ((this.teamId && !this.team?.hasPermission('ViewAnalytics'))
      || (!this.teamId && !this.store.user?.hasPermission('ViewAnalytics')))
      return this.setError(new Error(`Unauthorized`));

    await this.handleDateFilterChange();
    runInAction(() => {
      this.setLoaded();
    });
  }

  unmounted() {
    this.reset();
  }

  async setPageSize(pageSize: number) {
    this.pageSize = pageSize;
  }

  async handleDateFilterChange() {
    const { value: dateInputValue } = this.dateInput;

    if (dateInputValue) {
      const formattedStartDate = (dateInputValue as [Dayjs, Dayjs])[0].format();
      const formattedEndDate = (dateInputValue as [Dayjs, Dayjs])[1].format();

      this.timezone = DateTime.fromISO(formattedStartDate).zoneName;

      this.startDateFilter = DateTime
        .fromISO(formattedStartDate, { setZone: true })
        .set({ hour: 0, minute: 0, second: 0 })
        .setZone('UTC', { keepLocalTime: true });

      this.endDateFilter = DateTime
        .fromISO(formattedEndDate, { setZone: true })
        .set({ hour: 23, minute: 59, second: 59})
        .setZone('UTC', { keepLocalTime: true });

    } else {
      this.timezone = null;
      this.endDateFilter = null;
      this.startDateFilter = null;
      
    }

    await this.fetchData();

    // we can disable it because each chart will display its own error
    // if (res.some(([, err]) => !!err))
    //   this.setError();
  }

  async fetchData() {
    return Promise.all([
      this.topicViewsChart.fetch(),
      this.reactionsChart.fetch(),
      this.bookmarksChart.fetch(),
      this.audienceFocusChart.fetch(),
      this.teamPerformanceChart.fetch(),
      this.hoursCliprdChart.fetch(),
      this.performanceChart.fetch(),
      ...this.metricsWrapper.outputMetrics.map(metricCounter => metricCounter.fetch())
    ]);
  }

  // #region State helpers
  reset() {
    this.isLoading = false;
    this.error = null;
    this.teamId = null;
    this.dateInput.reset();
  }

  private setError(error?: Error) {
    if (!error)
      error = pageError('Unknown', 'An error occured while loading the page.');

    console.error(error);

    this.isLoading = false;
    this.error = error;
  }

  private setLoaded() {
    this.isLoading = false;
    this.error = null;
  }
  // #endregion
}
